// Inline announcement bar that sits below the page header (not floating).
// - Single broadcast: shows it statically.
// - Multiple broadcasts: auto-loops them with a smooth slide transition.
// - Clicking the bar opens a popup with the full list.
import { useEffect, useMemo, useState } from "react";
import { Megaphone, ArrowUpRight, Sparkles } from "lucide-react";
import { useActiveBroadcast, type Broadcast } from "@/hooks/useActiveBroadcast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const ROTATE_MS = 5000;

const openCta = (url?: string | null) => {
  if (!url) return;
  if (url.startsWith("/")) window.location.href = url;
  else window.open(url, "_blank", "noopener");
};

export const BroadcastBanner = () => {
  const { broadcasts, primary, readIds, dismissAll } = useActiveBroadcast();
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [enter, setEnter] = useState(true);

  // Highlight the primary (unread) first; otherwise just rotate everything.
  const ordered = useMemo<Broadcast[]>(() => {
    if (!broadcasts.length) return [];
    if (!primary) return broadcasts;
    const rest = broadcasts.filter((b) => b.id !== primary.id);
    return [primary, ...rest];
  }, [broadcasts, primary]);

  // Auto-rotate ticker with a brief exit/enter transition for that "high-end app" feel.
  useEffect(() => {
    if (ordered.length <= 1) return;
    const tick = setInterval(() => {
      setEnter(false);
      const swap = setTimeout(() => {
        setIndex((i) => (i + 1) % ordered.length);
        setEnter(true);
      }, 220);
      return () => clearTimeout(swap);
    }, ROTATE_MS);
    return () => clearInterval(tick);
  }, [ordered.length]);

  // Reset index when the list changes.
  useEffect(() => { setIndex(0); setEnter(true); }, [ordered.length]);

  if (!ordered.length) return null;
  const current = ordered[index] ?? ordered[0];
  const unreadCount = ordered.filter((b) => !readIds.has(b.id)).length;
  const hasCta = !!(current.cta_url && current.cta_label);

  return (
    <>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={cn(
            "group relative flex w-full items-center gap-3 overflow-hidden rounded-2xl border border-white/10 bg-card/70 px-3.5 py-3 text-left backdrop-blur-xl",
            "shadow-[0_8px_30px_-12px_rgba(0,0,0,0.5)] transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-elevated",
          )}
        >
          {/* Accent stripe */}
          <div className="absolute inset-y-0 left-0 w-[3px] bg-gradient-to-b from-primary via-primary/60 to-primary/10" />
          {/* Soft glow */}
          <div className="pointer-events-none absolute -right-12 -top-10 h-28 w-28 rounded-full bg-primary/15 blur-3xl" />

          {/* Icon chip */}
          <span className="relative ml-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 ring-1 ring-primary/25">
            <Megaphone className="h-4 w-4 text-primary" />
            {unreadCount > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-bold text-primary-foreground ring-2 ring-card">
                {unreadCount}
              </span>
            )}
          </span>

          {/* Sliding text content */}
          <div className="relative min-w-0 flex-1">
            <div
              key={current.id}
              className={cn(
                "min-w-0 transition-all duration-200 ease-out",
                enter ? "translate-y-0 opacity-100" : "-translate-y-1.5 opacity-0",
              )}
            >
              <div className="flex items-center gap-2">
                <span className="text-[9px] font-bold uppercase tracking-[0.16em] text-primary/80">
                  Announcement
                </span>
                {ordered.length > 1 && (
                  <span className="text-[9px] font-medium text-muted-foreground/70">
                    {index + 1}/{ordered.length}
                  </span>
                )}
              </div>
              <p className="mt-0.5 truncate text-sm font-semibold text-foreground">
                {current.title}
              </p>
              <p className="mt-0.5 truncate text-xs text-muted-foreground">
                {current.body}
              </p>
            </div>
          </div>

          <ArrowUpRight className="relative h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />

          {/* Ticker progress bar */}
          {ordered.length > 1 && (
            <div className="pointer-events-none absolute bottom-0 left-0 h-[2px] w-full overflow-hidden">
              <div
                key={`${current.id}-${index}`}
                className="h-full bg-gradient-to-r from-primary/40 via-primary to-primary/40"
                style={{
                  animation: `broadcast-progress ${ROTATE_MS}ms linear forwards`,
                }}
              />
            </div>
          )}
        </button>
      </div>

      {/* Inline keyframes for the progress bar (kept local to avoid touching tailwind config). */}
      <style>{`
        @keyframes broadcast-progress {
          from { transform: translateX(-100%); }
          to { transform: translateX(0%); }
        }
      `}</style>

      {/* "All announcements" dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg overflow-hidden border-white/10 bg-card/95 p-0 backdrop-blur-xl">
          <DialogHeader className="border-b border-white/10 bg-gradient-to-br from-primary/10 via-transparent to-transparent px-6 py-5">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/15 ring-1 ring-primary/25">
                <Sparkles className="h-4 w-4 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-base">What's new</DialogTitle>
                <DialogDescription className="text-xs">
                  {ordered.length} announcement{ordered.length === 1 ? "" : "s"}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="max-h-[60vh] space-y-3 overflow-y-auto px-5 py-5">
            {ordered.map((b) => {
              const isUnread = !readIds.has(b.id);
              return (
                <div
                  key={b.id}
                  className={cn(
                    "relative rounded-xl border p-4 transition-colors",
                    isUnread
                      ? "border-primary/30 bg-primary/5"
                      : "border-white/10 bg-background/40",
                  )}
                >
                  {isUnread && (
                    <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary ring-1 ring-primary/25">
                      New
                    </span>
                  )}
                  <p className="pr-14 text-sm font-semibold text-foreground">{b.title}</p>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{b.body}</p>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground/60">
                      {new Date(b.starts_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </span>
                    {b.cta_url && b.cta_label && (
                      <button
                        type="button"
                        onClick={() => openCta(b.cta_url)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary/80"
                      >
                        {b.cta_label}
                        <ArrowUpRight className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between gap-2 border-t border-white/10 px-5 py-3">
            <button
              type="button"
              onClick={() => { void dismissAll(); }}
              className="text-xs font-medium text-muted-foreground hover:text-foreground"
            >
              Mark all as read
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
            >
              Got it
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
